import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge, UrgencyBadge } from "@/pages/portal/badges";

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState({ sent: [], received: [] });
  const [network, setNetwork] = useState(0);
  const [brochureDownloads, setBrochureDownloads] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([api.get("/referrals"), api.get("/specialists")])
      .then(([r, s]) => {
        setData(r.data);
        setNetwork(s.data.length);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
    if (user?.role === "admin") {
      api.get("/admin/stats")
        .then((r) => setBrochureDownloads(r.data.brochure_downloads))
        .catch(() => {});
    }
  }, [user?.role]);

  const awaiting = data.received.filter((r) => r.status === "Pending").length;
  const stats = [
    { label: "Referrals Sent", value: data.sent.length, testid: "stat-sent" },
    { label: "Referrals Received", value: data.received.length, testid: "stat-received" },
    { label: "Awaiting Your Review", value: awaiting, testid: "stat-awaiting" },
    { label: "Network Specialists", value: network, testid: "stat-network" },
  ];
  if (brochureDownloads !== null) {
    stats.push({ label: "Brochure Downloads", value: brochureDownloads, testid: "stat-brochure-downloads" });
  }

  const recent = [...data.sent, ...data.received]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 5);

  return (
    <div data-testid="portal-dashboard">
      <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-gold mb-2">Portal Overview</p>
      <h1 className="font-serif text-3xl font-semibold text-navy mb-10">
        Good day, {user?.name?.split(" ")[0] === "Dr" ? user.name.split(" ").slice(0, 2).join(" ") : user?.name}.
      </h1>

      {loading ? (
        <p className="font-mono text-xs text-navy/40 tracking-widest" data-testid="dashboard-loading">LOADING…</p>
      ) : (
        <>
          <div className={`grid grid-cols-2 ${stats.length > 4 ? "lg:grid-cols-5" : "lg:grid-cols-4"} gap-px bg-navy/10 border border-navy/10 mb-12`} data-testid="dashboard-stats">
            {stats.map((s) => (
              <div key={s.label} className="bg-white p-6" data-testid={s.testid}>
                <p className="font-serif text-4xl font-semibold text-navy">{s.value}</p>
                <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-navy/50 mt-2">{s.label}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between mb-5">
            <h2 className="font-serif text-xl font-semibold text-navy">Recent Activity</h2>
            <Link
              to="/portal/referrals"
              data-testid="dashboard-new-referral-button"
              className="bg-navy text-ivory font-mono text-[10px] tracking-[0.2em] uppercase px-5 py-2.5 hover:bg-gold hover:text-navy transition-colors"
            >
              New Referral
            </Link>
          </div>

          {recent.length === 0 ? (
            <div className="bg-white border border-navy/10 p-10 text-center" data-testid="dashboard-empty">
              <p className="text-sm text-navy/50">
                No referrals yet. Open the directory to refer your first case to a colleague.
              </p>
            </div>
          ) : (
            <div className="bg-white border border-navy/10 divide-y divide-navy/5" data-testid="dashboard-recent-list">
              {recent.map((r) => (
                <div key={r.id} className="p-5 flex flex-wrap items-center gap-4 justify-between" data-testid={`recent-referral-${r.id}`}>
                  <div>
                    <p className="font-mono text-xs text-navy font-semibold">
                      PT {r.patient_initials} · {r.test_type}
                    </p>
                    <p className="text-xs text-navy/50 mt-1">
                      {r.from_name} → {r.to_name} · {new Date(r.created_at).toLocaleDateString("en-GB")}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <UrgencyBadge urgency={r.urgency} />
                    <StatusBadge status={r.status} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
